// Custom button matching the prototype's `Btn` from docs/screens/admin-ui.jsx.
// Variants: primary (ink), accent (palette deep), ghost, soft, danger.
//
// Polymorphic `as` prop: `<CButton as={Link} to="/auth?mode=signup">` renders
// a real <a> in the DOM so crawlers see href targets for primary CTAs
// (SEO-HYGIENE-1). Default remains <button>; existing onClick usages unchanged.
import { forwardRef, type ButtonHTMLAttributes, type ElementType, type ReactNode } from "react";
import { cn } from "@/lib/utils";

type Variant = "primary" | "accent" | "ghost" | "soft" | "danger";
type Size = "sm" | "md" | "lg";

interface CButtonOwnProps {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
  iconRight?: ReactNode;
  fullWidth?: boolean;
  as?: ElementType;
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
}

// Allow extra props (e.g. `to` for react-router Link) when `as` is supplied.
type CButtonProps = CButtonOwnProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof CButtonOwnProps | "type"> & {
    type?: ButtonHTMLAttributes<HTMLButtonElement>["type"];
    to?: string;
    href?: string;
  };

const SIZE_CLASSES: Record<Size, string> = {
  sm: "h-[34px] px-[14px] text-[13px]",
  md: "h-[42px] px-[18px] text-[14px]",
  lg: "h-[52px] px-[24px] text-[16px]",
};

const VARIANT_CLASSES: Record<Variant, string> = {
  primary: "bg-ink text-white border-0",
  accent: "bg-accent-deep text-white border-0",
  ghost: "bg-transparent text-ink border border-text-faint border-accent-deep",
  soft: "bg-black/5 text-ink border-0",
  danger: "bg-transparent text-accent-deep border border-border-danger",
};

export const CButton = forwardRef<HTMLElement, CButtonProps>(function CButton(
  { variant = "primary", size = "md", icon, iconRight, fullWidth, className, children, disabled, as, ...rest },
  ref,
) {
  const Component: ElementType = as ?? "button";
  const isButton = Component === "button";
  const extraProps: Record<string, unknown> = { ...rest };
  if (isButton) {
    extraProps.type = (rest as { type?: string }).type ?? "button";
    extraProps.disabled = disabled;
  } else if (disabled) {
    extraProps["aria-disabled"] = true;
  }

  return (
    <Component
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center gap-2 font-display font-semibold whitespace-nowrap rounded-md transition-[transform,background] duration-100 ease-out active:scale-[0.98] tracking-[0.02em]",
        SIZE_CLASSES[size],
        VARIANT_CLASSES[variant],
        disabled && "bg-border-soft text-text-faint cursor-not-allowed",
        fullWidth && "w-full",
        className,
      )}
      {...extraProps}
    >
      {icon}
      {children}
      {iconRight}
    </Component>
  );
});
